// src/components/UnitHistoryModal.js
import React, { useEffect, useState } from 'react';
import { FiActivity, FiArrowRight, FiX } from 'react-icons/fi';
import Modal from './Modal'; 
import { useApi } from '../hooks/useApi';

const UnitHistoryModal = ({ isOpen, onClose, serialNumber }) => {
    const [unit, setUnit] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const callApi = useApi();

    useEffect(() => {
        if (isOpen && serialNumber) {
            setLoading(true);
            setError(null);
            
            // Lookup specific unit by Serial Number
            callApi(`inventory/unit/lookup?sn=${encodeURIComponent(serialNumber)}`)
                .then(res => {
                    if (res.success) {
                        setUnit(res.data);
                    } else {
                        setError(res.error || 'Failed to load unit history.');
                    }
                    setLoading(false);
                });
        } else {
            setUnit(null);
        }
    }, [isOpen, serialNumber, callApi]);

    if (!isOpen) return null;

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('en-US', {
            month: 'short', day: 'numeric', year: '2-digit', hour: 'numeric', minute: '2-digit'
        });
    };

    const titleContent = (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FiActivity /> 
            <span>History: {serialNumber}</span>
        </div>
    );

    // --- GRID STYLES FOR SMART SCALING ---
    // 140px (Date) | 100px (Action) | 1fr (Details - Grows) | 120px (User)
    const gridRowStyle = {
        display: 'grid',
        gridTemplateColumns: '140px 100px 1fr 120px', 
        gap: '1rem',
        padding: '0.75rem 0.5rem',
        borderBottom: '1px solid var(--gray-100)',
        alignItems: 'start', // Align text to top for readability
        fontSize: '0.9rem'
    };

    const headerStyle = {
        ...gridRowStyle,
        fontWeight: '600',
        color: 'var(--gray-500)',
        borderBottom: '2px solid var(--gray-200)',
        backgroundColor: 'var(--gray-50)', // Subtle header background
        position: 'sticky',
        top: 0,
        zIndex: 1
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={titleContent}
            size="wide"
            footer={<button className="btn btn-secondary" onClick={onClose}>Close</button>}
        >
            {loading ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--gray-500)' }}>
                    <div className="loading-spinner" style={{ margin: '0 auto 1rem auto' }}></div>
                    <span>Locating unit history...</span>
                </div>
            ) : error ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--red-600)' }}>
                    <p>{error}</p>
                </div>
            ) : unit ? (
                <div className="table-wrapper" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    
                    {/* Header Summary Card */}
                    <div style={{ marginBottom:'1rem', padding:'0.75rem', background:'var(--blue-50)', borderRadius:'6px', border:'1px solid var(--blue-200)' }}>
                        <strong style={{ display:'block', marginBottom:'0.25rem', color:'var(--blue-800)' }}>{unit.name}</strong>
                        <span style={{ fontSize:'0.85rem', color:'var(--blue-700)' }}>
                            Current Status: <strong>{unit.status}</strong> 
                        </span>
                    </div>

                    {/* Scrollable Container for the Grid */}
                    <div style={{ overflowX: 'auto' }}>
                        <div style={{ minWidth: '600px' }}> {/* Ensures grid doesn't crush on mobile */}
                            
                            {/* Grid Header */}
                            <div style={headerStyle}>
                                <div>Date</div>
                                <div>Action</div>
                                <div>Details</div>
                                <div>User</div>
                            </div>

                            {/* Grid Rows */}
                            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                {unit.history && unit.history.length > 0 ? (
                                    [...unit.history].reverse().map((log, index) => (
                                        <div key={index} style={gridRowStyle}>
                                            {/* Date */}
                                            <div style={{ color: 'var(--gray-600)', whiteSpace: 'nowrap' }}>
                                                {formatDate(log.timestamp)}
                                            </div>
                                            
                                            {/* Action */}
                                            <div style={{ fontWeight: '600' }}>
                                                {log.action}
                                            </div>
                                            
                                            {/* Details (Flexes to fill space) */}
                                            <div style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                                                {/* Primary Rich Details */}
                                                {log.action === 'Transfer' && log.newOwnerName ? (
                                                    <span style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
                                                        {log.previousOwnerName || 'Unknown'} 
                                                        <FiArrowRight size={12} style={{ color: 'var(--gray-400)' }}/> 
                                                        {log.newOwnerName}
                                                    </span>
                                                ) : log.action === 'Ticket Usage' && log.newOwnerName ? (
                                                    <span>Used on <strong>{log.newOwnerName}</strong></span>
                                                ) : log.action === 'Created' && log.newOwnerName ? (
                                                    <span>Added to {log.newOwnerName}</span>
                                                ) : log.action === 'Return' && log.previousOwnerName ? (
                                                    <span>Returned from <strong>{log.previousOwnerName}</strong></span>
                                                ) : null}
                                                
                                                {/* Secondary Notes - Always visible if present */}
                                                {log.notes && (
                                                    <div style={{ fontSize: '0.85rem', color: 'var(--gray-500)', fontStyle: 'italic', marginTop: '0.25rem' }}>
                                                        {log.notes}
                                                    </div>
                                                )}
                                            </div>
                                            
                                            {/* User */}
                                            <div title={log.performedBy?.name || 'System'} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {log.performedBy?.name || 'System'}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--gray-500)', fontStyle: 'italic' }}>
                                        No history found for this unit.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </Modal>
    );
};

export default UnitHistoryModal;